import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { I, T } from "services/lang/LanguageService";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import useTimeState from "hooks/dashboard/useTimeState";
import { useClearDatasetData, useGetDataInfoRange } from "api/api-hooks/datasets-hooks";
import useChartInDialogStore from "store/chart-in-dialog-store";
import { registerAllModules } from "handsontable/registry";
import { CellChange } from "handsontable/common";
import { DatasetDataApi } from "api/DatasetDataApi";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { ChronoUnitLegacy, formatDisplayDatetime } from "utils/time-utils";
import { HotTable } from "@handsontable/react";
import { useTimezone } from "hooks/useTimezone";
import { calculateSteps, defaultFromDateTime, defaultToDateTime } from "../charts/utils";
import "handsontable/styles/handsontable.css";
import "handsontable/styles/ht-theme-main.css";
import "handsontable/styles/ht-theme-horizon.css";
import { DatasetDataInputTableProps, DatasetObject, DatasetValueMap, UpdateDatasetData } from "./DatasetDataInputTableTypes";
import "./DatasetDataInputTable.scss";
import { DATE_FORMATTER_MINUTES } from "utils/constants";

registerAllModules();

export default function DatasetDataInputTable(props: DatasetDataInputTableProps) {
  const tz = useTimezone();

  const { mutate } = useClearDatasetData();

  const [allSteps, setAllSteps] = useState<Array<Date>>([]);
  const [data, setData] = useState<Array<Array<number | string | null>>>([]);
  const [updatedDatasets, setUpdatedDatasets] = useState<Array<UpdateDatasetData>>([]);

  const { setDialogVisibility, setDateFrom, setDateTo, setHeader, setIdentifier, setIdentifiers } = useChartInDialogStore();

  const hotTableRef = useRef(null);

  const defaultFromDate = useMemo(
    () => props.from || defaultFromDateTime({ zoneId: tz.company, loadConf: props.loadConf }),
    [props.from, props.loadConf]
  );

  const defaultToDate = useMemo(
    () => props.to || defaultToDateTime({ zoneId: tz.company, loadConf: props.loadConf }),
    [props.to, props.loadConf]
  );

  const timeState = useTimeState({
    from: props.from || defaultFromDate.toString(),
    to: props.to || defaultToDate.toString(),
  });

  const { data: datasets } = useGetDataInfoRange(
    props.datasets.map(d => d.identifier),
    {
      timeStart: timeState.from?.toISOString() || new Date().toISOString(),
      timeEnd: timeState.to?.toISOString(),
    }
  );

  useEffect(() => {
    handleStepsCount();
  }, [timeState.from, timeState.to, datasets]);

  const openChartDialog = (identifier: string, identifiers?: string[], dateFrom?: Date, dateTo?: Date, header?: string) => {
    setIdentifier(identifier);
    setIdentifiers(identifiers || [identifier]);
    setDateFrom(dateFrom || new Date());
    setDateTo(dateTo || new Date());
    setHeader(header || T("word.chart"));
    setDialogVisibility(true);
  };

  const handleStepsCount = () => {
    if (timeState.from && timeState.to) {
      const stepsArr = calculateSteps(
        timeState.from,
        timeState.to,
        props?.stepSize || 1,
        props?.stepUnit || ChronoUnitLegacy.HOURS,
        tz.company
      );
      setAllSteps(props.sort === "ASC" ? stepsArr : stepsArr.reverse());
      if (datasets && stepsArr.length) {
        const initialData = stepsArr.map(step => {
          const row: Array<number | string | null> = [formatDisplayDatetime(step, tz.display, DATE_FORMATTER_MINUTES)];
          datasets.forEach(ds => {
            const dataPoint = ds.data.find(point => new Date(point.key).getTime() === step.getTime());
            row.push(dataPoint ? dataPoint.value : null);
          });
          return row;
        });
        setData(initialData);
      }
    }
  };

  const columns = datasets
    ? [
        {
          data: 0,
          title: T("word.date"),
          readOnly: true,
          width: 125,
        },
        ...datasets.map((ds, index) => {
          const overriddenTitle = props.datasets.find(d => d.identifier === ds.identifier)?.overriddenTitle;
          const title = `${overriddenTitle || ds.title || ds.identifier}${ds.unit ? `, ${ds.unit}` : ""}`;
          const charWidth = 9;
          const padding = 25;
          const calculatedWidth = title.length * charWidth + padding;

          return {
            data: index + 1,
            title: I(title),
            type: "numeric",
            allowInvalid: true,
            identifier: ds.identifier,
            width: Math.max(calculatedWidth, 70),
            validator: (value: any, callback: (isValid: boolean) => void) => {
              if (value === null || typeof value === "string") {
                if (value && value.length > 0) {
                  easasAlert({
                    summary: T("alert.error"),
                    detail: T("alert.invalid-input-numeric", { value }),
                    severity: "error",
                  });
                }
                callback(false);
              } else {
                callback(true);
              }
            },
          };
        }),
      ]
    : [];

  const handleAfterChange = (changes: CellChange[] | null) => {
    if (!changes) return;
    if (Array.isArray(changes)) {
      setData(prevData => {
        const newData = [...prevData];
        changes.forEach(([rowIndex, colIndex, oldValue, newValue]) => {
          if (oldValue !== newValue) {
            newData[rowIndex][colIndex as number] = newValue;

            const timestamp = newData[rowIndex]?.[0] !== null ? new Date(newData[rowIndex][0]).toISOString() : null;
            const column = columns[colIndex as number];

            if (!column) {
              easasAlert({
                summary: T("alert.error"),
                detail: "Failed to update data: Invalid column index",
                severity: "error",
              });
            }
            const { identifier } = column as DatasetObject;

            setUpdatedDatasets(prev => {
              const existing = prev.find(r => r.identifier === identifier);
              const updatedValues = existing?.values || {};
              if (timestamp !== null) {
                updatedValues[timestamp] = newValue;
              }

              return [...prev.filter(r => r.identifier !== identifier), { identifier, values: updatedValues }];
            });
          }
        });
        return newData;
      });
    }
  };

  const clearData = (dateFrom: Date, dateTo: Date, identifiers: string[]) => {
    const timeStart = dateFrom.toISOString();
    const timeEnd = dateTo.toISOString();
    const query = { timeStart, timeEnd, clearAll: false, identifiers };

    mutate(query, {
      onSuccess: () => {
        easasAlert({
          summary: T("alert.dataset-cleared"),
          detail: T("alert.data-has-been-cleared"),
          severity: "success",
        });
      },
      onError: error => {
        easasAlert({ summary: T("alert.clearing-failed"), detail: `${error}`, severity: "error" });
      },
    });
  };

  const updateData = () => {
    if (!updatedDatasets.length) {
      easasAlert({
        summary: T("alert.warn"),
        detail: T("alert.no-changes-made"),
        severity: "warn",
      });
      return;
    }

    const datasetsToClear: { dateFrom: Date; dateTo: Date; identifiers: string[] }[] = [];
    const validDatasets: UpdateDatasetData[] = [];

    updatedDatasets.forEach(dataset => {
      const validValues: DatasetValueMap = {};
      const identifiersToClear: string[] = [];

      Object.entries(dataset.values).forEach(([timestamp, value]) => {
        if (typeof value === "string" || value === null) {
          identifiersToClear.push(dataset.identifier);
          datasetsToClear.push({
            dateFrom: new Date(timestamp),
            dateTo: new Date(timestamp),
            identifiers: [dataset.identifier],
          });
        } else {
          validValues[timestamp] = value as number;
        }
      });

      if (Object.keys(validValues).length > 0) {
        validDatasets.push({
          identifier: dataset.identifier,
          values: validValues,
        });
      }
    });

    datasetsToClear.forEach(({ dateFrom, dateTo, identifiers }) => {
      clearData(dateFrom, dateTo, identifiers);
    });

    if (validDatasets.length > 0) {
      const formattedDatasets = validDatasets.map(dataset => ({
        identifier: dataset.identifier,
        values: Object.fromEntries(
          Object.entries(dataset.values)
            .filter(([value]) => value !== null)
            .map(([timestamp, value]) => [timestamp, Number(value)])
        ),
      }));
      DatasetDataApi.updateData(formattedDatasets)
        .then(() => {
          easasAlert({
            summary: T("alert.success"),
            detail: T("alert.datasets-updated"),
            severity: "success",
          });
          setUpdatedDatasets([]);
        })
        .catch(() => {
          easasAlert({
            summary: T("alert.error"),
            detail: T("alert.error-while-updating-datasets"),
            severity: "error",
          });
        });
    } else {
      easasAlert({
        summary: T("alert.warn"),
        detail: T("alert.no-valid-data-to-update"),
        severity: "warn",
      });
    }
  };

  return (
    <div className="dataset-data-input-table">
      <div className="flex flex-row gap-3 mb-3">
        <div className="flex flex-row gap-3 align-self-center">
          <CalendarWrapper id="from" value={timeState.from} onChange={timeState.setFrom} debounceTimeInMs={300} />
          <CalendarWrapper id="to" value={timeState.to} onChange={timeState.setTo} debounceTimeInMs={300} />
        </div>
        <Button
          label={T("action.update-datasets")}
          icon="pi pi-refresh"
          className="component-editor-button icon-blue"
          onClick={updateData}
          disabled={!updatedDatasets.length}
        />
      </div>
      <HotTable
        themeName="ht-theme-horizon"
        ref={hotTableRef}
        data={data}
        colHeaders={columns.map(col => col.title)}
        columnSorting
        manualColumnResize
        manualRowResize
        licenseKey="non-commercial-and-evaluation"
        afterChange={handleAfterChange}
        height={Math.min(allSteps.length * 25 + 40, props.tableHeight || 400)}
        columns={columns}
        dropdownMenu={{
          items: {
            openChart: {
              name: T("action.open-chart"),
              callback: (key, selection) => {
                const selectedColumn = selection[0]?.start?.col;
                const identifier = "identifier" in columns[selectedColumn] ? columns[selectedColumn].identifier : undefined;
                if (identifier) {
                  openChartDialog(identifier);
                }
              },
            },
          },
        }}
      />
    </div>
  );
}
